import { useEffect, useRef, useState } from "react";

interface UseScrollFadeInOptions {
  threshold?: number;
  delay?: number;
}

/**
 * スクロールでビューポートに入ったときにフェードインするフック
 * @returns { ref, style } — ref を要素に付け、style をインラインスタイルに渡す
 */
export function useScrollFadeIn<T extends HTMLElement = HTMLElement>(
  options: UseScrollFadeInOptions = {}
) {
  const { threshold = 0, delay = 0 } = options;
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(20px)",
    transition: `opacity 0.2s ease-out ${delay}s, transform 0.2s ease-out ${delay}s`,
  };

  return { ref, style, visible };
}

/**
 * 子要素を順番にフェードインさせるための遅延スタイルを生成
 */
export function staggerStyle(
  visible: boolean,
  index: number,
  baseDelay = 0.1,
  step = 0.12
): React.CSSProperties {
  const delay = baseDelay + index * step;
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(16px)",
    transition: `opacity 0.25s ease-out ${delay}s, transform 0.25s ease-out ${delay}s`,
  };
}
